/**
 * Shared styles for time reporting components.
 * Matches the ledgerloop-scheduler design system.
 */

// ─── COLORS ───
export const colors = {
  // Text — all meet WCAG 2.1 AA (4.5:1) on white for normal text
  textPrimary: '#111827',    // 15.4:1 ✓
  textSecondary: '#374151',  // 8.3:1  ✓
  textMuted: '#4b5563',      // 7.0:1  ✓ (was #6b7280 at 5.0:1)
  textLight: '#6b7280',      // 5.0:1  ✓ (was #9ca3af at 3.5:1 — FAILED)
  textDisabled: '#9ca3af',   // 3.5:1  — decorative only, not for readable text

  // Backgrounds
  bgPage: '#f8f8f8',
  bgCard: '#ffffff',
  bgSubtle: '#f9fafb',
  bgHover: '#f3f4f6',

  // Borders
  borderDefault: '#e5e7eb',
  borderLight: '#f1f5f9',
  borderDashed: '#d1d5db',

  // Primary (from app theme)
  primary: 'var(--primary)',
  primaryHover: 'var(--primary-hover)',
  primaryLight: 'var(--rgba-primary-1)',

  // Status — text colors darkened for AA on light backgrounds
  success: '#047857',        // 5.3:1 on successLight ✓ (was #059669)
  successLight: 'rgba(5, 120, 87, 0.1)',
  warning: '#b45309',        // 5.7:1 on warningLight ✓ (was #d97706)
  warningLight: 'rgba(180, 83, 9, 0.1)',
  danger: '#dc2626',         // 5.0:1 on dangerLight ✓ (was #ef4444)
  dangerLight: 'rgba(220, 38, 38, 0.08)',
  info: '#2563eb',           // 4.6:1 on infoLight ✓ (was #3b82f6)
  infoLight: 'rgba(37, 99, 235, 0.08)',
};

// ─── TYPOGRAPHY TOKENS ───
// Use these instead of hardcoding fontSize/fontWeight/lineHeight.
// Named by purpose, not by size — so if we change the scale, everything updates.

export const text = {
  // Big numbers: total hours, KPI values
  heading: { fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: colors.textPrimary },

  // Card titles, section headers
  title: { fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: colors.textPrimary },

  // Default readable text
  body: { fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: colors.textSecondary },

  // Compact body (tables, lists, forms)
  bodySmall: { fontSize: 13, fontWeight: 400, lineHeight: 1.5, color: colors.textSecondary },

  // Emphasized compact text (project names, row labels)
  bodySmallMedium: { fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: colors.textPrimary },

  // Form labels, section subtitles, helper text
  label: { fontSize: 12, fontWeight: 500, lineHeight: 1.5, color: colors.textLight },

  // Secondary info, descriptions, timestamps
  caption: { fontSize: 11, fontWeight: 400, lineHeight: 1.4, color: colors.textMuted },

  // Badges, metadata, frequency counts, column headers
  micro: { fontSize: 10, fontWeight: 600, lineHeight: 1.4, color: colors.textLight },

  // Section headers (uppercase labels like "PROJEKT", "SENASTE PERIODER")
  sectionHeader: { fontSize: 11, fontWeight: 500, lineHeight: 1.4, color: colors.textLight, letterSpacing: '0.05em', textTransform: 'uppercase' },

  // Tabular numbers (hours, amounts)
  number: { fontVariantNumeric: 'tabular-nums' },
};

// ─── SHARED COMPONENT STYLES ───

export const card = {
  background: colors.bgCard,
  borderRadius: 12,
  border: 'none',
  overflow: 'hidden',
};

export const cardHeader = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: `1px solid ${colors.borderLight}`,
};

export const cardTitle = {
  ...text.title,
  margin: 0,
};

export const sectionTitle = {
  ...text.sectionHeader,
};

// ─── TABLE STYLES ───

export const table = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
};

export const tableHeaderRow = {
  borderBottom: `1px solid ${colors.borderDefault}`,
};

export const tableHeaderCell = {
  padding: '8px 12px',
  fontSize: 11,
  fontWeight: 500,
  color: colors.textLight,
  letterSpacing: '0.02em',
  textAlign: 'left',
  background: colors.bgSubtle,
};

export const tableHeaderCellCenter = {
  ...tableHeaderCell,
  textAlign: 'center',
};

export const tableRow = {
  borderBottom: `1px solid ${colors.borderLight}`,
  transition: 'background 100ms ease',
  cursor: 'pointer',
};

export const tableCell = {
  padding: '8px 12px',
  fontSize: 13,
  color: colors.textSecondary,
  verticalAlign: 'middle',
};

export const tableCellCenter = {
  ...tableCell,
  textAlign: 'center',
};

// ─── BADGE STYLES ───

export const badge = (color, bg) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 10,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 99,
  color: color,
  backgroundColor: bg,
  whiteSpace: 'nowrap',
  lineHeight: '16px',
});

export const statusBadge = {
  open: badge(colors.textMuted, colors.bgHover),
  submitted: badge(colors.warning, colors.warningLight),
  approved: badge(colors.success, colors.successLight),
  rejected: badge(colors.danger, colors.dangerLight),
  locked: badge('#64748b', 'rgba(100,116,139,0.08)'),
  draft: badge(colors.textMuted, colors.bgHover),
};

// Swedish labels for the period status enum. Used to localize the status
// pills so we don't render raw English values like "submitted" / "approved"
// to a Swedish-first user. Phase 3.7 minimum-viable i18n: ship the visible
// string fix without the multi-day react-i18next scaffolding cost. The full
// i18n rollout is Phase 4 work and will absorb this map.
export const statusLabel = {
  open: 'Öppen',
  draft: 'Utkast',
  submitted: 'Inskickad',
  approved: 'Godkänd',
  rejected: 'Avvisad',
  locked: 'Låst',
};

export const billingBadge = {
  time_materials: badge('var(--primary)', 'var(--rgba-primary-1)'),
  fixed_price: badge(colors.warning, colors.warningLight),
  retainer: badge('#7c3aed', 'rgba(124,58,237,0.08)'),
  non_billable: badge(colors.textMuted, colors.bgHover),
};

export const billingLabel = {
  time_materials: 'T&M',
  fixed_price: 'Fastpris',
  retainer: 'Retainer',
  non_billable: 'Intern',
};

// ─── BUTTON STYLES ───

export const btnPrimary = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 6,
  border: 'none',
  fontSize: 13,
  fontWeight: 600,
  color: '#fff',
  background: 'var(--primary)',
  cursor: 'pointer',
  transition: 'all 150ms ease',
};

export const btnSecondary = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 6,
  border: `1px solid ${colors.borderDefault}`,
  fontSize: 13,
  fontWeight: 500,
  color: colors.textSecondary,
  background: colors.bgCard,
  cursor: 'pointer',
  transition: 'all 150ms ease',
};

export const btnGhost = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 6,
  border: 'none',
  fontSize: 13,
  fontWeight: 500,
  color: colors.textMuted,
  background: 'transparent',
  cursor: 'pointer',
  transition: 'all 150ms ease',
};

export const btnDashed = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '10px 16px',
  borderRadius: 8,
  border: `1.5px dashed ${colors.borderDashed}`,
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--primary)',
  background: 'transparent',
  cursor: 'pointer',
  transition: 'all 150ms ease',
  width: '100%',
};

export const btnIcon = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  borderRadius: 8,
  border: `1px solid ${colors.borderDefault}`,
  background: colors.bgCard,
  cursor: 'pointer',
  color: colors.textMuted,
  transition: 'all 150ms ease',
};

export const btnDanger = {
  ...btnIcon,
  border: `1px solid #fecaca`,
  color: colors.danger,
};

// ─── TIMESHEET CELL STYLES ───

export const timesheetCell = {
  width: 56,
  height: 30,
  borderRadius: 6,
  border: `1.5px dashed #c5cad0`,
  background: 'transparent',
  fontSize: 12,
  fontWeight: 500,
  color: colors.textDisabled,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 120ms ease',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const timesheetCellFilled = {
  ...timesheetCell,
  border: `1px solid ${colors.borderDefault}`,
  background: colors.bgSubtle,
  color: colors.textPrimary,
  fontWeight: 600,
};

export const timesheetCellEditing = {
  width: 56,
  height: 30,
  borderRadius: 6,
  border: '2px solid var(--primary)',
  background: 'var(--rgba-primary-1)',
  fontSize: 12,
  fontWeight: 600,
  color: colors.textPrimary,
  textAlign: 'center',
  outline: 'none',
  boxShadow: '0 0 0 3px rgba(68,129,78,0.12)',
};

export const timesheetCellReadonly = {
  ...timesheetCell,
  border: '1px solid transparent',
  cursor: 'default',
  color: colors.textDisabled,
};

// ─── INPUT STYLES ───

export const input = {
  borderRadius: 8,
  border: `1px solid ${colors.borderDefault}`,
  fontSize: 14,
  height: 40,
  padding: '0 12px',
  transition: 'border-color 150ms ease, box-shadow 150ms ease',
  outline: 'none',
  width: '100%',
};

export const inputFocus = {
  borderColor: 'var(--primary)',
  boxShadow: '0 0 0 3px rgba(68,129,78,0.12)',
};

// ─── AVATAR ───

export const avatar = (size = 36) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: size * 0.36,
  fontWeight: 600,
  flexShrink: 0,
  background: 'var(--rgba-primary-1)',
  color: 'var(--primary)',
});

// ─── COLOR DOT ───
// User-defined project colors may be light (e.g., #ffff00), so always add a
// subtle border to keep the dot visible on white backgrounds.

export const colorDot = (color, size = 10) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  backgroundColor: color || colors.textMuted,
  border: '1px solid rgba(0,0,0,0.1)',
  flexShrink: 0,
  display: 'inline-block',
});

// ─── EMPTY STATE ───

export const emptyState = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  textAlign: 'center',
};

export const emptyStateIcon = {
  marginBottom: 16,
  opacity: 0.3,
  color: 'var(--primary)',
};

export const emptyStateTitle = {
  fontSize: 17,
  fontWeight: 700,
  color: colors.textPrimary,
  marginBottom: 8,
};

export const emptyStateDesc = {
  fontSize: 14,
  color: colors.textLight,
  maxWidth: 360,
  marginBottom: 24,
  lineHeight: 1.5,
};

// ─── STAT CARD ───

export const statCard = {
  background: colors.bgCard,
  borderRadius: 12,
  padding: '18px 22px',
  border: `1px solid rgba(0,0,0,0.04)`,
  display: 'flex',
  alignItems: 'center',
  gap: 14,
};

export const statIcon = (color) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  background: `${color}12`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color,
});

export const statLabel = {
  fontSize: 11,
  fontWeight: 500,
  color: colors.textLight,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  marginBottom: 2,
};

export const statValue = {
  fontSize: 22,
  fontWeight: 700,
  color: colors.textPrimary,
  letterSpacing: '-0.02em',
  lineHeight: 1,
};

export const statUnit = {
  fontSize: 13,
  fontWeight: 400,
  color: colors.textLight,
};

// ─── SEARCH ───

export const searchContainer = {
  position: 'relative',
  maxWidth: 320,
};

export const searchIcon = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: colors.textLight,
};

export const searchInput = {
  width: '100%',
  paddingLeft: 38,
  paddingRight: 12,
  height: 38,
  borderRadius: 8,
  border: `1px solid ${colors.borderDefault}`,
  fontSize: 13,
  outline: 'none',
  transition: 'border-color 150ms ease',
};

// ─── MODAL ───

export const modalHeader = {
  padding: '20px 24px',
  borderBottom: `1px solid ${colors.borderLight}`,
};

export const modalTitle = {
  fontSize: 17,
  fontWeight: 700,
  color: colors.textPrimary,
};

export const modalSubtitle = {
  fontSize: 13,
  color: colors.textLight,
  margin: '4px 0 0',
};

export const modalBody = {
  padding: 24,
};

export const modalFooter = {
  padding: '16px 24px',
  borderTop: `1px solid ${colors.borderLight}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
};

export const modalLabel = {
  fontSize: 13,
  color: colors.textSecondary,
  marginBottom: 6,
  display: 'block',
  fontWeight: 500,
};

// ─── REACT-SELECT STYLES ───

export const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? 'var(--primary)' : colors.borderDefault,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(68,129,78,0.12)' : 'none',
    fontSize: 14,
    '&:hover': { borderColor: state.isFocused ? 'var(--primary)' : '#d1d5db' },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    border: `1px solid ${colors.borderDefault}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    zIndex: 1050,
    fontSize: 14,
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--rgba-primary-1)' : 'white',
    color: isSelected ? 'white' : colors.textPrimary,
    padding: '8px 12px',
    fontSize: 14,
  }),
  placeholder: (base) => ({ ...base, color: colors.textLight, fontSize: 14 }),
  singleValue: (base) => ({ ...base, fontSize: 14 }),
};

// Compact variant for inline table editing (timesheet rows)
export const selectStylesCompact = {
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    height: 30,
    borderRadius: 6,
    borderColor: state.isFocused ? 'var(--primary)' : colors.borderDefault,
    boxShadow: state.isFocused ? '0 0 0 2px rgba(68,129,78,0.10)' : 'none',
    fontSize: 12,
    '&:hover': { borderColor: state.isFocused ? 'var(--primary)' : '#d1d5db' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 28 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    border: `1px solid ${colors.borderDefault}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    zIndex: 1050,
    fontSize: 12,
    minWidth: 200,
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--rgba-primary-1)' : 'white',
    color: isSelected ? 'white' : colors.textPrimary,
    padding: '6px 10px',
    fontSize: 12,
  }),
  placeholder: (base) => ({ ...base, color: colors.textLight, fontSize: 12 }),
  singleValue: (base) => ({ ...base, fontSize: 12 }),
  groupHeading: (base) => ({ ...base, fontSize: 10, fontWeight: 600, color: colors.textLight, padding: '4px 10px', textTransform: 'uppercase' }),
};

// ─── FOCUS-VISIBLE CSS (inject via <style> tag in page components) ───

export const focusVisibleCSS = `
  .tr-card button:focus-visible,
  .tr-card input:focus-visible,
  .tr-card [role="button"]:focus-visible,
  .tr-card select:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
  .tr-card *:focus:not(:focus-visible) {
    outline: none;
  }
`;
